
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Subscription } from '../types';

interface UseFetchOperationProps {
  userId: string | undefined;
  isMountedRef: React.MutableRefObject<boolean>;
  setIsSubscribed: (isSubscribed: boolean) => void;
  setCurrentSubscription: (subscription: Subscription | null) => void;
  setLastChecked?: (date: Date | null) => void;
  showToast?: boolean;
}

export function useFetchOperation({
  userId,
  isMountedRef,
  setIsSubscribed,
  setCurrentSubscription,
  setLastChecked,
  showToast = false,
}: UseFetchOperationProps) {
  const { toast } = useToast();

  const fetchProfileSubscription = async (): Promise<{ isProTier: boolean; profile: any } | null> => {
    if (!userId || !isMountedRef.current) return null;
    
    try {
      console.log('=== PROFILE FETCH DEBUG ===');
      console.log('Fetching profile for user:', userId);
      
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('subscription_tier')
        .eq('id', userId)
        .single();
      
      if (profileError) {
        console.error('❌ Error fetching profile:', profileError);
        if (isMountedRef.current && showToast) {
          toast({
            title: "Failed to refresh subscription",
            description: "We couldn't check your subscription status. Please try again.",
            variant: "destructive",
          });
        }
        return null;
      }
      
      if (!isMountedRef.current) {
        return null;
      }
      
      console.log('✅ Profile data successfully fetched:', profile);
      console.log('Raw subscription_tier value:', profile?.subscription_tier);
      
      // Check for Pro tier - this is the authoritative source
      const isProTier = profile?.subscription_tier === 'pro';
      console.log('🔍 Is Pro tier result:', isProTier);
      
      // Immediately update the subscription state
      console.log('🚀 Setting isSubscribed to:', isProTier);
      setIsSubscribed(isProTier);
      
      return { 
        isProTier,
        profile
      };
    } catch (error) {
      console.error('💥 Exception in fetchProfileSubscription:', error);
      return null;
    }
  };

  const fetchUserSubscriptions = async (userId: string): Promise<Subscription | null> => {
    if (!isMountedRef.current) return null;
    
    try {
      console.log('=== SUBSCRIPTION FETCH DEBUG ===');
      console.log('Fetching subscriptions for user:', userId);
      
      const { data: subscriptions, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (subscriptionError) {
        console.error('❌ Error fetching subscription:', subscriptionError);
        return null;
      }
      
      if (!isMountedRef.current) {
        return null;
      }
      
      console.log('✅ Subscription data successfully fetched:', subscriptions);
      
      return (subscriptions && subscriptions.length > 0) ? subscriptions[0] as Subscription : null;
    } catch (error) {
      console.error('💥 Exception in fetchUserSubscriptions:', error);
      return null;
    }
  };

  return {
    fetchProfileSubscription,
    fetchUserSubscriptions
  };
}
